/**
 * Print areas attached to the variations of a PRINT product (flyers, displays...),
 * for the current reseller product.
 *
 * A PRINT product has no print area of its own: the reseller defines here, per
 * group of variations (rules on the configurator steps, or variation codes one
 * by one), the finished format with its lost border (bleed) and security border,
 * and the techniques the editor offers. Only the editor uses them: never the
 * webshop, never priced.
 */
define('pimVariationPrintAreasView',[
  'module',
  'marionette',
  'jquery',
  'underscore',
  'app',
  'bootbox',
  'settings',
  'template!pimVariationPrintAreasTpl',
  'pimVariationPrintAreaEditView'
], function (
  module,
  Marionette,
  $,
  _,
  App,
  bootbox,
  Settings,
  viewTpl,
  EditView
) {
  'use strict';

  /** Text in the user's language, else English, else the first one there is. */
  function translate(name) {
    var values = name && name.get ? name.get('values') : (name && name.values);
    if (!values) {
      return '';
    }
    var lang = (Settings.get('lang') || '').toLowerCase();
    var found = _.find(_.keys(values), function (key) {
      return key.toLowerCase() === lang && values[key];
    }) || _.find(_.keys(values), function (key) {
      return key.toLowerCase() === 'en' && values[key];
    }) || _.find(_.keys(values), function (key) {
      return !!values[key];
    });
    return found ? values[found] : '';
  }

  function formatNumber(value) {
    if (value === null || value === undefined || value === '') {
      return '0';
    }
    return String(Math.round(Number(value) * 100) / 100);
  }

  /** "3 mm" when the four sides are equal, "top/right/bottom/left mm" otherwise. */
  function borderSummary(border) {
    var sides = _.map(['top', 'right', 'bottom', 'left'], function (side) {
      return formatNumber(border ? border[side] : 0);
    });
    if (_.uniq(sides).length === 1) {
      return sides[0] + ' mm';
    }
    return sides.join('/') + ' mm';
  }

  function errorMessage(xhr, fallback) {
    if (xhr && xhr.responseText && xhr.responseText.length < 500 && xhr.responseText.charAt(0) !== '<') {
      return xhr.responseText;
    }
    return fallback;
  }

  var VariationPrintAreasView = Marionette.ItemView.extend({
    template: viewTpl,
    className: 'pim-vpa',

    events: {
      'click .js-vpa-add': 'onAddClick',
      'click .js-vpa-edit': 'onEditClick',
      'click .js-vpa-delete': 'onDeleteClick'
    },

    initialize: function () {
      this.areas = [];
      this.counts = {};
      this.loading = true;
      this.error = null;
      this.load();
    },

    getResProduct: function () {
      return this.options.pimProduct.getCurrentResProduct();
    },

    load: function () {
      var resProduct = this.getResProduct();
      if (!resProduct) {
        this.loading = false;
        return;
      }
      this.loading = true;
      this.error = null;
      $.when(App.request('pim:products:variation-print-areas', resProduct.get('id')))
        .done(_.bind(function (areas) {
          this.areas = _.sortBy(areas || [], function (area) {
            return area.displayOrder || 0;
          });
          this.counts = {};
          this.loading = false;
          this.safeRender();
          this.loadCounts();
        }, this))
        .fail(_.bind(function (xhr) {
          this.loading = false;
          this.error = errorMessage(xhr, _.i18n('pim.variationPrintAreas.loadError', null, 'Unable to load the variation print areas'));
          this.safeRender();
        }, this));
    },

    /** How many variations each area applies to, one preview call per area. */
    loadCounts: function () {
      var resProduct = this.getResProduct();
      if (!resProduct) {
        return;
      }
      _.each(this.areas, function (area) {
        $.when(App.request('pim:products:variation-print-area:preview', resProduct.get('id'), area))
          .done(_.bind(function (result) {
            this.counts[area.id] = result ? result.count : 0;
            this.safeRender();
          }, this))
          .fail(_.bind(function () {
            this.counts[area.id] = '?';
            this.safeRender();
          }, this));
      }, this);
    },

    safeRender: function () {
      if (!this.isDestroyed) {
        this.render();
      }
    },

    serializeData: function () {
      var counts = this.counts;
      return {
        hasResProduct: !!this.getResProduct(),
        loading: this.loading,
        error: this.error,
        areas: _.map(this.areas, function (area) {
          return {
            id: area.id,
            code: area.code,
            name: translate(area.name),
            format: formatNumber(area.width) + ' \u00d7 ' + formatNumber(area.height) + ' mm',
            lostBorder: borderSummary(area.lostBorder),
            securityBorder: borderSummary(area.securityBorder),
            techniques: _.map(_.sortBy(area.printZones || [], function (zone) {
              return zone.displayOrder || 0;
            }), function (zone) {
              var tech = zone.imprintTechnology || {};
              return {
                code: tech.code || '',
                name: translate(tech.name),
                isDefault: !!zone.isDefault
              };
            }),
            count: _.has(counts, area.id) ? counts[area.id] : null,
            rules: (area.appliesTo || []).length,
            extraCodes: (area.variationCodes || []).length
          };
        })
      };
    },

    findArea: function (e) {
      var id = $(e.currentTarget).closest('[data-id]').attr('data-id');
      return _.find(this.areas, function (area) {
        return area.id === id;
      });
    },

    onAddClick: function () {
      this.openEditor(null);
    },

    onEditClick: function (e) {
      var area = this.findArea(e);
      if (area) {
        this.openEditor(area);
      }
    },

    openEditor: function (area) {
      var resProduct = this.getResProduct();
      if (!resProduct) {
        return;
      }
      var popup = new EditView({
        pimProduct: this.options.pimProduct,
        resProductId: resProduct.get('id'),
        // A deep copy: the list keeps the saved version until the server answers
        area: area ? JSON.parse(JSON.stringify(area)) : null,
        displayOrder: this.areas.length
      });
      popup.on('saved', _.bind(function () {
        this.load();
      }, this));
      popup.show({
        title: area ?
          _.i18n('pim.variationPrintAreas.editTitle', null, 'Edit the variation print area') + ' ' + _.escape(area.code) :
          _.i18n('pim.variationPrintAreas.newTitle', null, 'New variation print area'),
        className: 'pim-vpa-modal'
      });
    },

    onDeleteClick: function (e) {
      var area = this.findArea(e);
      var resProduct = this.getResProduct();
      if (!area || !resProduct) {
        return;
      }
      bootbox.confirm(_.i18n('pim.variationPrintAreas.confirmDelete', null, 'Delete this print area? Compositions already made on it lose their zone.') +
        ' (' + _.escape(area.code) + ')', _.bind(function (result) {
        if (!result) {
          return;
        }
        $.when(App.request('pim:products:variation-print-area:delete', resProduct.get('id'), area.id))
          .done(_.bind(function () {
            this.load();
          }, this))
          .fail(function (xhr) {
            bootbox.alert(_.escape(errorMessage(xhr, _.i18n('pim.variationPrintAreas.deleteError', null, 'Unable to delete the print area'))));
          });
      }, this));
    }
  });

  module.exports = VariationPrintAreasView;
});

